import React, { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import toast from "react-hot-toast";
import { Calendar } from "lucide-react";
import { request } from "../../utils/api";
import { StatisticsSkeleton } from "../Skeletons";
import { BRAND_THEME } from "../../ui/brandTheme";
import { getLocalSystemDate, displayDateUS } from "../../utils/dateUtils";

const STATUS_CHART_COLORS = {
  PreAdmit: "#0ea5e9",
  Admit: "#059669",
  Active: "#059669",
  Hold: "#ea580c",
  Inactive: "#94a3b8",
  Discharged: "#94a3b8",
  NonAdmit: "#7c3aed",
  Deceased: "#57534e",
};

/** YYYY-MM-01 for the current local month. */
function currentMonthStartIso() {
  const today = getLocalSystemDate();
  return `${today.slice(0, 7)}-01`;
}

function parseYmdLocal(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toYmdLocal(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const MetricRow = ({ label, value, emphasize, muted }) => (
  <div
    className={`flex items-center justify-between gap-4 px-4 py-2.5 border-b border-indigo-50/80 last:border-0 ${
      emphasize ? "bg-indigo-50/40" : ""
    }`}
  >
    <span
      className={`text-[13px] leading-none ${
        emphasize
          ? "font-semibold text-indigo-900"
          : muted
            ? "text-slate-500"
            : "text-slate-600"
      }`}
    >
      {label}
    </span>
    <span
      className={`text-[13px] tabular-nums leading-none shrink-0 ${
        emphasize
          ? "font-bold text-indigo-900"
          : "font-semibold text-slate-800"
      }`}
    >
      {value}
    </span>
  </div>
);

const DateField = ({ id, label, value, onChange, maxDate, minDate }) => (
  <div className="flex items-center gap-2">
    <label
      htmlFor={id}
      className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 shrink-0"
    >
      {label}
    </label>
    <div className="relative">
      <DatePicker
        id={id}
        selected={parseYmdLocal(value)}
        onChange={(date) => onChange(toYmdLocal(date) || getLocalSystemDate())}
        dateFormat="MM/dd/yyyy"
        placeholderText="MM/DD/YYYY"
        maxDate={maxDate || undefined}
        minDate={minDate || undefined}
        className={`h-8 w-[8.75rem] pl-2.5 pr-8 text-[13px] rounded-md border border-indigo-200 bg-white text-slate-800 outline-none transition-colors hover:border-indigo-300 ${BRAND_THEME.fieldFocus}`}
        calendarClassName="!shadow-lg !border-indigo-200 !rounded-xl"
        popperClassName="!z-[100]"
        showPopperArrow={false}
      />
      <Calendar
        size={13}
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-indigo-400"
        aria-hidden
      />
    </div>
  </div>
);

const SectionCard = ({ title, children, className = "" }) => (
  <div
    className={`bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col ${BRAND_THEME.section.wrap} ${className}`}
  >
    <div
      className={`flex items-center gap-2 h-10 px-4 border-b shrink-0 ${BRAND_THEME.section.head}`}
    >
      <span
        className={`w-0.5 h-3.5 rounded-full ${BRAND_THEME.section.bar}`}
        aria-hidden
      />
      <h4
        className={`text-[11px] font-semibold uppercase tracking-wide ${BRAND_THEME.section.title}`}
      >
        {title}
      </h4>
    </div>
    {children}
  </div>
);

/**
 * Date-filtered panel: From/To + Census summary + Patient distribution.
 * Changes when the date range changes; independent of Total Patient Summary.
 */
const PatientPeriodInsights = () => {
  const today = getLocalSystemDate();
  const monthStart = currentMonthStartIso();
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPeriod = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ fromDate: from, toDate: to });
      const res = await request(
        `/dashboard/patients/period?${qs.toString()}`,
      );
      if (res?.status === 1 && res.result) {
        setData(res.result);
      } else {
        throw new Error(res?.message || "Failed to load period insights");
      }
    } catch (err) {
      toast.error(err.message || "Failed to load period insights");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fromDate || !toDate) return;
    if (fromDate > toDate) {
      toast.error("From date cannot be after To date");
      return;
    }
    fetchPeriod(fromDate, toDate);
  }, [fromDate, toDate, fetchPeriod]);

  const handleFromChange = (next) => {
    setFromDate(next);
    if (toDate && next > toDate) setToDate(next);
  };

  const handleToChange = (next) => {
    setToDate(next);
    if (fromDate && next < fromDate) setFromDate(next);
  };

  const resetToday = () => {
    setFromDate(currentMonthStartIso());
    setToDate(getLocalSystemDate());
  };

  const chartData = useMemo(() => {
    const dist = data?.distribution || [];
    return dist
      .filter((d) => d.count > 0)
      .map((d) => ({
        name: d.label,
        status: d.status,
        value: d.count,
        percent: d.percent,
      }));
  }, [data]);

  const rangeLabel = useMemo(() => {
    const from = displayDateUS(data?.fromDate || fromDate);
    const to = displayDateUS(data?.toDate || toDate);
    if (from && to && from === to) return from;
    return `${from || "—"} – ${to || "—"}`;
  }, [data, fromDate, toDate]);

  const totalPatients = data?.totals?.total ?? 0;

  return (
    <section
      className={`bg-white rounded-xl border shadow-sm overflow-hidden ${BRAND_THEME.section.wrap}`}
    >
      {/* Date filter — only for this panel */}
      <div
        className={`flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between px-3.5 py-2.5 border-b ${BRAND_THEME.section.head}`}
      >
        <div className="min-w-0">
          <h3
            className={`text-sm font-semibold truncate ${BRAND_THEME.section.title}`}
          >
            Period insights
          </h3>
          <p className="text-[11px] text-slate-500 tabular-nums truncate">
            {rangeLabel}
            {loading ? " · Updating…" : ""}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <DateField
            id="patient-period-from"
            label="From"
            value={fromDate}
            onChange={handleFromChange}
            maxDate={parseYmdLocal(toDate) || parseYmdLocal(today)}
          />
          <span
            className="hidden sm:inline text-slate-300 select-none"
            aria-hidden
          >
            —
          </span>
          <DateField
            id="patient-period-to"
            label="To"
            value={toDate}
            onChange={handleToChange}
            minDate={parseYmdLocal(fromDate)}
            maxDate={parseYmdLocal(today)}
          />
          <button
            type="button"
            onClick={resetToday}
            className="h-8 px-2.5 text-[11px] font-semibold rounded-md border border-indigo-200 bg-indigo-50/60 text-indigo-700 hover:bg-indigo-100 transition-colors"
          >
            This month
          </button>
        </div>
      </div>

      <div className="p-3 bg-slate-50/40">
        {loading && !data ? (
          <StatisticsSkeleton />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <SectionCard title="Census summary">
              <div className="py-0.5">
                <MetricRow
                  label="Active census (Admit + Active)"
                  value={data?.totals?.activeCensus ?? 0}
                  emphasize
                />
                <MetricRow
                  label="Pending patients (Pre-Admit)"
                  value={data?.totals?.pending ?? 0}
                />
                <MetricRow
                  label="On hold"
                  value={data?.totals?.onHold ?? 0}
                />
                <MetricRow
                  label="Inactive"
                  value={data?.totals?.inactive ?? 0}
                />
                <MetricRow
                  label="Discharged"
                  value={data?.totals?.discharged ?? 0}
                />
                <MetricRow
                  label="Referrals (in range)"
                  value={data?.periodActivity?.referrals ?? 0}
                  muted
                />
                <MetricRow
                  label="SOC / admits (in range)"
                  value={data?.periodActivity?.admits ?? 0}
                  muted
                />
                <MetricRow
                  label="EOC / discharges (in range)"
                  value={data?.periodActivity?.discharges ?? 0}
                  muted
                />
                <MetricRow
                  label="Net change in census"
                  value={data?.periodActivity?.netChange ?? 0}
                  emphasize
                />
                <MetricRow
                  label="Total patient records"
                  value={totalPatients}
                  emphasize
                />
              </div>
            </SectionCard>

            <SectionCard title="Patient distribution">
              <div className="flex flex-col sm:flex-row sm:items-stretch gap-0 flex-1 min-h-[220px]">
                <div className="relative flex items-center justify-center sm:w-[48%] sm:border-r border-indigo-50 px-3 py-4 min-h-[180px]">
                  {chartData.length === 0 ? (
                    <p className="text-sm text-slate-400">
                      No data for this range
                    </p>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={168}>
                        <PieChart>
                          <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={48}
                            outerRadius={68}
                            paddingAngle={2}
                            cornerRadius={3}
                            stroke="none"
                          >
                            {chartData.map((entry) => (
                              <Cell
                                key={entry.status}
                                fill={
                                  STATUS_CHART_COLORS[entry.status] ||
                                  "#6366f1"
                                }
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name, item) => [
                              `${value} (${item?.payload?.percent ?? 0}%)`,
                              name,
                            ]}
                            contentStyle={{
                              borderRadius: 8,
                              borderColor: "#c7d2fe",
                              fontSize: 12,
                              boxShadow:
                                "0 4px 12px rgba(79,70,229,0.08)",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-xl font-bold text-slate-900 tabular-nums leading-none">
                          {totalPatients}
                        </span>
                        <span className="text-[10px] uppercase tracking-wide text-slate-400 mt-1">
                          Total
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <ul className="flex-1 px-3.5 py-3 space-y-1.5 content-center">
                  {(data?.distribution || []).map((row) => (
                    <li
                      key={row.status}
                      className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-indigo-50/50 transition-colors"
                    >
                      <span className="inline-flex items-center gap-2 min-w-0">
                        <span
                          className="h-2 w-2 rounded-full shrink-0 ring-2 ring-white shadow-sm"
                          style={{
                            backgroundColor:
                              STATUS_CHART_COLORS[row.status] || "#6366f1",
                          }}
                        />
                        <span className="text-[12px] text-slate-600 truncate">
                          {row.label}
                        </span>
                      </span>
                      <span className="text-[12px] tabular-nums shrink-0">
                        <span className="font-semibold text-slate-800">
                          {row.count}
                        </span>
                        <span className="text-slate-400 ml-1.5">
                          {row.percent}%
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </SectionCard>
          </div>
        )}
      </div>
    </section>
  );
};

export default PatientPeriodInsights;
